import { createSlice } from "@reduxjs/toolkit";
import { WIDGET_CONSTANTS } from "../../constant/ChatbotWidgetConstant";
import { transformWidgetToReduxState } from "../../utils/webChatWidgetPayload";

// Initial state for all chatbot branding tabs
const initialState = {
  appearance: {
    widgetIcon: {
      file: null,
      preview: null,
      error: null,
      isUploading: false,
    },
    widgetHeader: {
      value: WIDGET_CONSTANTS.DEFAULT_VALUES.WIDGET_HEADER,
      error: null,
      isDirty: false,
    },
    welcomeMessage: {
      value: WIDGET_CONSTANTS.DEFAULT_VALUES.WELCOME_MESSAGE,
      error: null,
      isDirty: false,
    },
    brandColor: {
      value: WIDGET_CONSTANTS.DEFAULT_VALUES.BRAND_COLOR,
      error: null,
      isDirty: false,
    },
    buttonColor: {
      value: WIDGET_CONSTANTS.DEFAULT_VALUES.BUTTON_COLOR,
      error: null,
      isDirty: false,
    },
    launcherIcon: {
      file: null,
      preview: null,
      error: null,
      isUploading: false,
    },
    launcherText: {
      value: WIDGET_CONSTANTS.DEFAULT_VALUES.TEXT_US,
      error: null,
      isDirty: false,
    },
    textUsColor: {
      value: WIDGET_CONSTANTS.DEFAULT_VALUES.TEXT_US_COLOR,
      error: null,
      isDirty: false,
    },
    launcherColor: {
      value: WIDGET_CONSTANTS.DEFAULT_VALUES.LAUNCHER_COLOR,
      error: null,
      isDirty: false,
    },
    removeBranding: {
      value: WIDGET_CONSTANTS.DEFAULT_VALUES.REMOVE_BRANDING,
      error: null,
      isDirty: false,
    },
    showMessageTextbox: {
      value: WIDGET_CONSTANTS.DEFAULT_VALUES.SHOW_MESSAGE_TEXTBOX,
      error: null,
      isDirty: false,
    },
  },
  content: {
    topics: [], // Start with empty topics - users will add their own
    isLoading: false,
    isSaving: false,
    hasUnsavedChanges: false,
  },
  preferences: {
    trustedDomains: {
      domains: [],
      isLoading: false,
      error: null,
    },
    forwardConversation: {
      mode: "user", // "user" or "team"
      selectedUsers: [], // Array for multiple user selection
      selectedTeams: [], // Array for multiple team selection
      isLoading: false,
      error: null,
    },
    conversationModes: {
      chatWithUs: true,
      textUs: true,
      isLoading: false,
      error: null,
    },
    askForLocation: {
      enabled: false,
      isLoading: false,
      error: null,
    },
    showLeadHelpWithOptions: {
      enabled: false,
      isLoading: false,
      error: null,
    },
    isLoading: false,
    isSaving: false,
    hasUnsavedChanges: false,
  },
  embedCode: {
    // Future implementation for embed code tab
  },
  widget: {
    id: null,
    name: "",
    widgetUid: null, // Widget UID from server response
    mode: WIDGET_CONSTANTS.WIDGET_MODES.CREATE,
    data: null, // Full widget data for editing
    originalData: null, // Original transformed data for revert functionality
    isLoading: false,
    // Progressive tab properties
    currentStep: 0, // 0: appearance, 1: content, 2: preferences, 3: embedCode
    completedSteps: [], // Array of completed step indices
    accumulatedData: {
      appearance: null,
      content: null,
      preferences: null,
    },
    isCreating: false,
    isUpdating: false,
    widgetLocationId: null,
  },
  isLoading: false,
  isSaving: false,
  lastSaved: null,
  hasUnsavedChanges: false,
};

const chatbotBrandingSlice = createSlice({
  name: "chatbotBranding",
  initialState,
  reducers: {
    // Update appearance field values
    updateAppearanceField: (state, action) => {
      const { fieldName, value } = action.payload;

      if (state.appearance[fieldName]) {
        if (fieldName === "widgetIcon" || fieldName === "launcherIcon") {
          // Handle file fields
          if (value === null) {
            // Clear file field completely when null is passed
            state.appearance[fieldName] = {
              file: null,
              preview: null,
              error: null,
              isUploading: false,
            };
          } else {
            // Update file field with new values
            state.appearance[fieldName] = {
              ...state.appearance[fieldName],
              ...value,
            };
          }
        } else {
          // Handle text, color, and toggle fields
          state.appearance[fieldName].value = value;
          state.appearance[fieldName].isDirty = true;
          state.appearance[fieldName].error = null;
        }
        state.hasUnsavedChanges = true;
      }
    },

    // Set field error
    setFieldError: (state, action) => {
      const { fieldName, error, section = "appearance" } = action.payload;

      if (state[section][fieldName]) {
        state[section][fieldName].error = error;
      }
    },

    // Clear field error
    clearFieldError: (state, action) => {
      const { fieldName, section = "appearance" } = action.payload;

      if (state[section][fieldName]) {
        state[section][fieldName].error = null;
      }
    },

    // Clear all errors in a section
    clearAllErrors: (state, action) => {
      const { section = "appearance" } = action.payload;

      Object.keys(state[section]).forEach(fieldName => {
        if (state[section][fieldName] && typeof state[section][fieldName] === "object") {
          state[section][fieldName].error = null;
        }
      });
    },

    // Set loading state
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    // Set saving state
    setSaving: (state, action) => {
      state.isSaving = action.payload;
    },

    // Set file upload state
    setFileUploadState: (state, action) => {
      const { fieldName, isUploading } = action.payload;

      if (state.appearance[fieldName]) {
        state.appearance[fieldName].isUploading = isUploading;
      }
    },

    // Clear file field
    clearFileField: (state, action) => {
      const { fieldName } = action.payload;

      if (
        state.appearance[fieldName] &&
        (fieldName === "widgetIcon" || fieldName === "launcherIcon")
      ) {
        state.appearance[fieldName] = {
          file: null,
          preview: null,
          error: null,
          isUploading: false,
        };
        state.hasUnsavedChanges = true;
      }
    },

    // Reset appearance to defaults
    resetAppearance: state => {
      state.appearance = initialState.appearance;
      state.hasUnsavedChanges = false;
    },

    // Reset specific section
    resetSection: (state, action) => {
      const { section } = action.payload;

      if (state[section] && initialState[section]) {
        state[section] = initialState[section];
        state.hasUnsavedChanges = false;
      }
    },

    // Mark as saved
    markAsSaved: state => {
      state.hasUnsavedChanges = false;
      state.lastSaved = new Date().toISOString();
      state.isSaving = false;

      // Clear dirty flags
      Object.keys(state.appearance).forEach(fieldName => {
        if (state.appearance[fieldName] && typeof state.appearance[fieldName] === "object") {
          state.appearance[fieldName].isDirty = false;
        }
      });
    },

    // Validate field
    validateField: (state, action) => {
      const { fieldName, value, config } = action.payload;
      let error = null;

      // Required field validation
      if (config.required && (!value || value.toString().trim() === "")) {
        error = "fieldRequired";
      }

      // Text length validation
      if (
        !error &&
        config.validation?.maxLength &&
        value &&
        value.length > config.validation.maxLength
      ) {
        error = "textTooLong";
      }

      // Color format validation
      if (
        !error &&
        config.type === "color" &&
        value &&
        !WIDGET_CONSTANTS.VALIDATION.HEX_COLOR_REGEX.test(value)
      ) {
        error = "invalidColor";
      }

      if (state.appearance[fieldName]) {
        state.appearance[fieldName].error = error;
      }
    },

    // Bulk update appearance fields (for form initialization)
    bulkUpdateAppearance: (state, action) => {
      const fields = action.payload;

      Object.entries(fields).forEach(([fieldName, value]) => {
        if (state.appearance[fieldName]) {
          if (fieldName === "widgetIcon" || fieldName === "launcherIcon") {
            if (value === null) {
              // Clear file field completely when null is passed
              state.appearance[fieldName] = {
                file: null,
                preview: null,
                error: null,
                isUploading: false,
              };
            } else {
              state.appearance[fieldName] = {
                ...state.appearance[fieldName],
                ...value,
              };
            }
          } else {
            state.appearance[fieldName].value = value;
            state.appearance[fieldName].isDirty = false;
          }
        }
      });

      state.hasUnsavedChanges = false;
    },

    // Content actions
    updateContentTopic: (state, action) => {
      const { id, updates } = action.payload;
      const topicIndex = state.content.topics.findIndex(topic => topic.id === id);

      if (topicIndex !== -1) {
        state.content.topics[topicIndex] = {
          ...state.content.topics[topicIndex],
          ...updates,
        };
        state.content.hasUnsavedChanges = true;
        state.hasUnsavedChanges = true;
      }
    },

    toggleContentTopic: (state, action) => {
      const { id } = action.payload;
      const topicIndex = state.content.topics.findIndex(topic => topic.id === id);

      if (topicIndex !== -1) {
        state.content.topics[topicIndex].enabled = !state.content.topics[topicIndex].enabled;
        state.content.hasUnsavedChanges = true;
        state.hasUnsavedChanges = true;
      }
    },

    reorderContentTopics: (state, action) => {
      const { topics } = action.payload;
      state.content.topics = topics.map((topic, index) => ({
        ...topic,
        order: index + 1,
      }));
      state.content.hasUnsavedChanges = true;
      state.hasUnsavedChanges = true;
    },

    deleteContentTopic: (state, action) => {
      const { id } = action.payload;
      state.content.topics = state.content.topics.filter(topic => topic.id !== id);
      state.content.hasUnsavedChanges = true;
      state.hasUnsavedChanges = true;
    },

    addContentTopic: (state, action) => {
      const { topic } = action.payload;
      const maxId = Math.max(...state.content.topics.map(t => t.id), 0);
      const newTopic = {
        ...topic,
        id: maxId + 1,
        order: state.content.topics.length + 1,
      };
      state.content.topics.push(newTopic);
      state.content.hasUnsavedChanges = true;
      state.hasUnsavedChanges = true;
    },

    setContentLoading: (state, action) => {
      state.content.isLoading = action.payload;
    },

    setContentSaving: (state, action) => {
      state.content.isSaving = action.payload;
    },

    markContentAsSaved: state => {
      state.content.hasUnsavedChanges = false;
      state.content.isSaving = false;
    },

    resetContentToDefault: state => {
      const isEditMode = state.widget.mode === WIDGET_CONSTANTS.WIDGET_MODES.EDIT;
      const originalContent = state.widget.originalData?.content;

      if (isEditMode && originalContent) {
        // In edit mode, revert to original widget data
        state.content = {
          ...originalContent,
          isLoading: false,
          isSaving: false,
          hasUnsavedChanges: false,
        };
      } else {
        // In create mode, reset to empty topics
        state.content = {
          topics: [], // Reset to empty topics - users will add their own
          isLoading: false,
          isSaving: false,
          hasUnsavedChanges: false,
        };
      }
    },

    // Preferences actions
    addTrustedDomain: (state, action) => {
      const { domain } = action.payload;
      if (domain && !state.preferences.trustedDomains.domains.includes(domain)) {
        state.preferences.trustedDomains.domains.push(domain);
        state.preferences.hasUnsavedChanges = true;
      }
    },

    removeTrustedDomain: (state, action) => {
      const { index } = action.payload;
      state.preferences.trustedDomains.domains.splice(index, 1);
      state.preferences.hasUnsavedChanges = true;
    },

    updateTrustedDomain: (state, action) => {
      const { index, domain } = action.payload;
      if (index >= 0 && index < state.preferences.trustedDomains.domains.length) {
        state.preferences.trustedDomains.domains[index] = domain;
        state.preferences.hasUnsavedChanges = true;
      }
    },

    setTrustedDomainsError: (state, action) => {
      state.preferences.trustedDomains.error = action.payload;
    },

    clearTrustedDomainsError: state => {
      state.preferences.trustedDomains.error = null;
    },

    setPreferencesLoading: (state, action) => {
      state.preferences.isLoading = action.payload;
    },

    setPreferencesSaving: (state, action) => {
      state.preferences.isSaving = action.payload;
    },

    markPreferencesAsSaved: state => {
      state.preferences.hasUnsavedChanges = false;
      state.preferences.isSaving = false;
    },

    resetPreferencesToDefault: state => {
      const isEditMode = state.widget.mode === WIDGET_CONSTANTS.WIDGET_MODES.EDIT;
      const originalPreferences = state.widget.originalData?.preferences;

      if (isEditMode && originalPreferences) {
        // In edit mode, revert to original widget data
        state.preferences = {
          ...originalPreferences,
          isLoading: false,
          isSaving: false,
          hasUnsavedChanges: false,
        };
      } else {
        // In create mode, reset to defaults
        state.preferences = {
          trustedDomains: {
            domains: [],
            isLoading: false,
            error: null,
          },
          forwardConversation: {
            mode: "user",
            selectedUsers: [],
            selectedTeams: [],
            isLoading: false,
            error: null,
          },
          conversationModes: {
            chatWithUs: true,
            textUs: true,
            isLoading: false,
            error: null,
          },
          askForLocation: {
            enabled: false,
            isLoading: false,
            error: null,
          },
          showLeadHelpWithOptions: {
            enabled: false,
            isLoading: false,
            error: null,
          },
          isLoading: false,
          isSaving: false,
          hasUnsavedChanges: false,
        };
      }
    },

    resetAppearanceToDefault: state => {
      const isEditMode = state.widget.mode === WIDGET_CONSTANTS.WIDGET_MODES.EDIT;
      const originalAppearance = state.widget.originalData?.appearance;

      if (isEditMode && originalAppearance) {
        // In edit mode, revert to original widget data
        state.appearance = originalAppearance;
      } else {
        // In create mode, reset to defaults (this will be handled by the component)
        // The component will call bulkUpdateAppearance with default values
      }
    },

    // Forward Conversation actions
    updateForwardConversationMode: (state, action) => {
      const { mode } = action.payload;
      state.preferences.forwardConversation.mode = mode;
      // Clear selections when mode changes
      state.preferences.forwardConversation.selectedUsers = [];
      state.preferences.forwardConversation.selectedTeams = [];
      state.preferences.hasUnsavedChanges = true;
    },

    updateSelectedUsers: (state, action) => {
      const { userIds } = action.payload;
      state.preferences.forwardConversation.selectedUsers = userIds;
      state.preferences.hasUnsavedChanges = true;
    },

    updateSelectedTeams: (state, action) => {
      const { teamIds } = action.payload;
      state.preferences.forwardConversation.selectedTeams = teamIds;
      state.preferences.hasUnsavedChanges = true;
    },

    // Conversation Modes actions
    updateConversationMode: (state, action) => {
      const { mode, enabled } = action.payload;
      state.preferences.conversationModes[mode] = enabled;
      state.preferences.hasUnsavedChanges = true;
    },

    // Ask for Location actions
    updateAskForLocation: (state, action) => {
      const { enabled } = action.payload;
      state.preferences.askForLocation.enabled = enabled;
      state.preferences.hasUnsavedChanges = true;
    },

    // Show customer service / warranty option actions
    updateShowLeadHelpWithOptions: (state, action) => {
      const { enabled } = action.payload;
      state.preferences.showLeadHelpWithOptions.enabled = enabled;
      state.preferences.hasUnsavedChanges = true;
    },

    // Widget management actions
    initializeWidget: (state, action) => {
      const { widget, mode } = action.payload;
      const isCreateMode = mode === WIDGET_CONSTANTS.WIDGET_MODES.CREATE;

      // Transform widget data for edit mode
      const transformedData = !isCreateMode && widget ? transformWidgetToReduxState(widget) : null;

      state.widget = {
        id: widget?.id || null,
        // Preserve existing name for create mode, use widget name for edit mode
        name: isCreateMode ? state.widget.name : widget?.metaData?.widgetName || "",
        widgetUid: widget?.widgetUid || null, // Store widget UID from existing widget data
        mode: mode || WIDGET_CONSTANTS.WIDGET_MODES.CREATE,
        data: widget || null,
        originalData: transformedData, // Store original transformed data for revert functionality
        isLoading: false,
        // Progressive tab initialization
        currentStep: isCreateMode ? 0 : 3, // Create starts at 0, edit allows all tabs
        completedSteps: isCreateMode ? [] : [0, 1, 2, 3], // Edit mode has all steps completed
        accumulatedData: {
          appearance: null,
          content: null,
          preferences: null,
        },
        isCreating: false,
        isUpdating: false,
        widgetLocationId: isCreateMode ? state.widget.widgetLocationId : widget?.locationId,
      };

      // Populate form data for edit mode
      if (transformedData) {
        state.appearance = transformedData.appearance;
        state.content = transformedData.content;
        state.preferences = transformedData.preferences;
      }
    },

    setWidgetName: (state, action) => {
      state.widget.name = action.payload;
    },

    setWidgetLocationId: (state, action) => {
      state.widget.widgetLocationId = action.payload;
    },

    setWidgetUid: (state, action) => {
      state.widget.widgetUid = action.payload;
    },

    setWidgetMode: (state, action) => {
      state.widget.mode = action.payload;
    },

    setWidgetId: (state, action) => {
      state.widget.id = action.payload;
    },

    clearWidget: state => {
      state.widget = {
        id: null,
        name: "",
        widgetUid: null,
        mode: WIDGET_CONSTANTS.WIDGET_MODES.CREATE,
        data: null,
        originalData: null,
        isLoading: false,
        currentStep: 0,
        completedSteps: [],
        accumulatedData: {
          appearance: null,
          content: null,
          preferences: null,
        },
        isCreating: false,
        isUpdating: false,
      };
    },

    setWidgetLoading: (state, action) => {
      state.widget.isLoading = action.payload;
    },

    // Progressive tab actions
    advanceToNextStep: state => {
      const nextStep = state.widget.currentStep + 1;
      if (nextStep <= 3) {
        // Mark current step as completed before advancing
        if (!state.widget.completedSteps.includes(state.widget.currentStep)) {
          state.widget.completedSteps.push(state.widget.currentStep);
        }
        state.widget.currentStep = nextStep;
      }
    },

    accumulateStepData: (state, action) => {
      const { step, data } = action.payload;
      const stepNames = ["appearance", "content", "preferences"];
      if (stepNames[step]) {
        state.widget.accumulatedData[stepNames[step]] = data;
      }
    },

    setCreating: (state, action) => {
      state.widget.isCreating = action.payload;
    },

    setUpdating: (state, action) => {
      state.widget.isUpdating = action.payload;
    },

    resetWidgetProgress: state => {
      state.widget.currentStep = 0;
      state.widget.completedSteps = [];
      state.widget.accumulatedData = {
        appearance: null,
        content: null,
        preferences: null,
      };
      state.widget.isCreating = false;
      state.widget.isUpdating = false;
    },
  },
});

// Export actions
export const {
  updateAppearanceField,
  setFieldError,
  clearFieldError,
  clearAllErrors,
  setLoading,
  setSaving,
  setFileUploadState,
  clearFileField,
  resetAppearance,
  resetSection,
  markAsSaved,
  validateField,
  bulkUpdateAppearance,
  updateContentTopic,
  toggleContentTopic,
  reorderContentTopics,
  deleteContentTopic,
  addContentTopic,
  setContentLoading,
  setContentSaving,
  markContentAsSaved,
  addTrustedDomain,
  removeTrustedDomain,
  updateTrustedDomain,
  setTrustedDomainsError,
  clearTrustedDomainsError,
  setPreferencesLoading,
  setPreferencesSaving,
  markPreferencesAsSaved,
  updateForwardConversationMode,
  updateSelectedUsers,
  updateSelectedTeams,
  updateConversationMode,
  updateAskForLocation,
  updateShowLeadHelpWithOptions,
  resetPreferencesToDefault,
  resetContentToDefault,
  resetAppearanceToDefault,
  initializeWidget,
  setWidgetName,
  setWidgetUid,
  setWidgetMode,
  setWidgetId,
  clearWidget,
  setWidgetLoading,
  advanceToNextStep,
  accumulateStepData,
  setCreating,
  setUpdating,
  resetWidgetProgress,
  setWidgetLocationId,
} = chatbotBrandingSlice.actions;

// Selectors
export const selectChatbotBranding = state => state.chatbotBranding;
export const selectAppearanceState = state => state.chatbotBranding.appearance;
export const selectFieldValue = fieldName => state =>
  state.chatbotBranding.appearance[fieldName]?.value;
export const selectFieldError = fieldName => state =>
  state.chatbotBranding.appearance[fieldName]?.error;
export const selectFieldState = fieldName => state => state.chatbotBranding.appearance[fieldName];
export const selectHasErrors = state => {
  return Object.values(state.chatbotBranding.appearance).some(
    field => field && typeof field === "object" && field.error
  );
};
export const selectHasUnsavedChanges = state => state.chatbotBranding.hasUnsavedChanges;
export const selectIsLoading = state => state.chatbotBranding.isLoading;
export const selectIsSaving = state => state.chatbotBranding.isSaving;
export const selectLastSaved = state => state.chatbotBranding.lastSaved;

// Preferences selectors
export const selectPreferencesState = state => state.chatbotBranding.preferences;
export const selectTrustedDomains = state =>
  state.chatbotBranding.preferences.trustedDomains.domains;
export const selectTrustedDomainsError = state =>
  state.chatbotBranding.preferences.trustedDomains.error;
export const selectPreferencesIsLoading = state => state.chatbotBranding.preferences.isLoading;
export const selectPreferencesIsSaving = state => state.chatbotBranding.preferences.isSaving;
export const selectPreferencesHasUnsavedChanges = state =>
  state.chatbotBranding.preferences.hasUnsavedChanges;

// Content selectors
export const selectContentState = state => state.chatbotBranding.content;
export const selectContentTopics = state => state.chatbotBranding.content.topics;
export const selectContentIsLoading = state => state.chatbotBranding.content.isLoading;
export const selectContentIsSaving = state => state.chatbotBranding.content.isSaving;
export const selectContentHasUnsavedChanges = state =>
  state.chatbotBranding.content.hasUnsavedChanges;

// Widget selectors
export const selectCurrentWidget = state => state.chatbotBranding.widget;
export const selectWidgetName = state => state.chatbotBranding.widget.name;
export const selectWidgetLocationId = state => state.chatbotBranding.widget.widgetLocationId;
export const selectWidgetUid = state => state.chatbotBranding.widget.widgetUid;
export const selectWidgetMode = state => state.chatbotBranding.widget.mode;
export const selectWidgetData = state => state.chatbotBranding.widget.data;
export const selectWidgetIsLoading = state => state.chatbotBranding.widget.isLoading;
export const selectWidgetCurrentStep = state => state.chatbotBranding.widget.currentStep;
export const selectWidgetCompletedSteps = state => state.chatbotBranding.widget.completedSteps;
export const selectWidgetAccumulatedData = state => state.chatbotBranding.widget.accumulatedData;
export const selectWidgetIsCreating = state => state.chatbotBranding.widget.isCreating;
export const selectWidgetIsUpdating = state => state.chatbotBranding.widget.isUpdating;

// Export reducer
export default chatbotBrandingSlice.reducer;
